import { useRef, useEffect, RefObject } from "react";
import gsap from "gsap";

export function useHeroVideoAnimation(
  heroRef: RefObject<HTMLDivElement | null>,
  videoRef: RefObject<HTMLVideoElement | null>
) {
  const animationDone = useRef(false);

  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    // On mobile, show the first frame as a static image and skip scroll animation
    if (isMobile) {
      const video = videoRef.current;
      if (video) {
        const showFirstFrame = () => {
          video.currentTime = 0.01;
          video.pause();
        };

        if (video.readyState >= 2) {
          showFirstFrame();
        } else {
          video.addEventListener('loadeddata', showFirstFrame, { once: true });
          const playPromise = video.play();
          if (playPromise) {
            playPromise.then(() => {
              video.pause();
              video.currentTime = 0.01;
            }).catch(() => {});
          }
        }
      }
      animationDone.current = true;
      return;
    }

    const MOUSE_STEP = 0.5;
    const TOUCH_STEP = 0.04;
    const SCROLL_DISTANCE = 5000;
    const LERP = 0.3;

    // targetTime: where scroll wants the video to be (updated instantly by events)
    // smoothTime: the value we actually apply to video.currentTime (interpolated)
    let targetTime = 0;
    let smoothTime = 0;
    let isSeeking = false;

    const video = videoRef.current!;

    // Track when the browser finishes a seek — avoids queueing seeks that choke the decoder
    const onSeeked = () => { isSeeking = false; };
    video.addEventListener("seeked", onSeeked);

    // GSAP ticker runs every frame — lerp smoothTime toward targetTime, apply once
    const onTick = () => {
      const v = videoRef.current;
      if (!v || !v.duration) return;

      const diff = targetTime - smoothTime;
      if (Math.abs(diff) < 0.001) return;        // already there
      if (isSeeking) return;                       // wait for previous seek to finish

      smoothTime += diff * LERP;
      isSeeking = true;
      v.currentTime = smoothTime;

      if (smoothTime >= v.duration - 0.05) {
        animationDone.current = true;
      } else if (smoothTime <= 0.05) {
        animationDone.current = false;
      }
    };

    gsap.ticker.add(onTick);

    const handleWheel = (e: WheelEvent) => {
      const v = videoRef.current;
      if (!v || !v.duration) return;

      const duration = v.duration;
      const absDelta = Math.abs(e.deltaY);
      const step = absDelta < 50
        ? (absDelta / SCROLL_DISTANCE) * duration
        : MOUSE_STEP;

      // Scrolling down — scrub video forward (hijack scroll)
      if (!animationDone.current && e.deltaY > 0 && targetTime < duration - 0.05) {
        e.preventDefault();
        targetTime = Math.min(targetTime + step, duration);
        return;
      }

      // Scrolling up at page top — scrub video backward
      if (!animationDone.current && e.deltaY < 0 && window.scrollY === 0) {
        e.preventDefault();
        targetTime = Math.max(targetTime - step, 0);
        return;
      }

      // Video done, scrolling back up from page top — re-enter scrubbing
      if (animationDone.current && e.deltaY < 0 && window.scrollY === 0) {
        e.preventDefault();
        animationDone.current = false;
        targetTime = Math.max(targetTime - step, 0);
      }
    };

    let touchStartY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const v = videoRef.current;
      if (!v || !v.duration) return;

      const deltaY = touchStartY - e.touches[0].clientY;
      touchStartY = e.touches[0].clientY;

      if (!animationDone.current && deltaY > 0 && targetTime < v.duration - 0.05) {
        e.preventDefault();
        targetTime = Math.min(targetTime + TOUCH_STEP, v.duration);
        return;
      }

      if (!animationDone.current && deltaY < 0 && window.scrollY === 0) {
        e.preventDefault();
        targetTime = Math.max(targetTime - TOUCH_STEP, 0);
        return;
      }

      if (animationDone.current && deltaY < 0 && window.scrollY === 0) {
        e.preventDefault();
        animationDone.current = false;
        targetTime = Math.max(targetTime - TOUCH_STEP, 0);
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      gsap.ticker.remove(onTick);
      video.removeEventListener("seeked", onSeeked);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, [heroRef, videoRef]);
}
